import { useI18n } from 'next-localization';

import { createEvent, DateTime } from 'ics';

import { NeuButton } from '@hcahealthcaredpx/neutron-react';

import { ConfirmationData } from '../../booking.types';

type CreateCalendarButtonProps = {
  appointment: ConfirmationData;
};

export const CreateCalendarButton = ({ appointment }: CreateCalendarButtonProps): JSX.Element => {
  const { t } = useI18n();

  const getApptEndTime = (startTime: string, duration: number): string => {
    const [hour, minute] = startTime.split(':').map(Number);
    const totalMinutes = hour * 60 + minute + duration;
    const endHour = Math.floor(totalMinutes / 60);
    const endMinute = totalMinutes % 60;

    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(endHour)}:${pad(endMinute)}`;
  };

  const createIcs = () => {
    const appointmentData = {
      appointmentTitle: t('OAS.Booking.Confirmation.DoctorAppointment', {
        reasonForVisit: appointment.reasonForVisit,
      }),
      date: appointment.dateTimeOfAppointment.substring(0, 8),
      time: appointment.dateTimeOfAppointment.substring(8),
      location: `${appointment.location.name}, ${appointment.location.street}, ${appointment.location.city}, ${appointment.location.state}, ${appointment.location.zip}`,
      duration: Number(appointment._response?.minutes) || 60, // fallback to 60 minutes if not provided
    };

    const [year, month, day] = [
      Number(appointmentData.date.substring(0, 4)),
      Number(appointmentData.date.substring(4, 6)),
      Number(appointmentData.date.substring(6, 8)),
    ];
    const startTime = appointmentData.time;
    const endTime = getApptEndTime(startTime, appointmentData.duration);

    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    const start: DateTime = [year, month, day, startHour, startMinute];
    const end: DateTime = [year, month, day, endHour, endMinute];

    const event = {
      start,
      end,
      title: appointmentData.appointmentTitle,
      description: '',
      location: appointmentData.location,
    };

    createEvent(event, (error, value) => {
      if (error) {
        console.error('Error creating calendar event:', error);
        return;
      }

      let blob;
      try {
        blob = new Blob([value], { type: 'text/calendar;charset=utf-8' });
      } catch (error) {
        console.error('Error creating Blob for ICS file:', error);
        return;
      }
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `doctor-appointment-${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}.ics`;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    });
  };

  return (
    <NeuButton className="w-100 d-print-none" onClick={createIcs}>
      {t('OAS.Booking.Confirmation.AddToCalendar')}
    </NeuButton>
  );
};
